import React, { useEffect, useRef, useState } from 'react'

import s from './Select.module.scss'

import { useShowDropdown } from 'hooks/useShowDropdown'

import { ArrowDownIcon } from 'assets/icons/ArrowDownIcon'
import { LockIcon } from 'assets/icons/LockIcon'

import { ControlLabel } from 'components/UI/ControlLabel/ControlLabel'
import { Spinner } from 'components/UI/Spinner/Spinner'

export interface Option {
  value: string | number
  label: string
  disabled?: boolean
}

export interface SelectProps {
  options: Option[]
  optionsIcons?: { [key: string]: React.ReactNode }
  onChange: (option: Option) => void
  name: string
  value: Option
  icon?: React.ReactNode
  disabled?: boolean
  placeholder?: string
  label?: React.ReactNode
  customInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  className?: string
  labelClassName?: string
  /** only for AsyncSelect component */
  loading?: boolean
}

interface SelectableOption extends Option {
  selected?: boolean
}

export function Select({
  options,
  optionsIcons,
  label,
  onChange,
  name,
  icon,
  disabled,
  placeholder,
  customInputChange,
  loading,
  className,
  value,
  labelClassName,
}: SelectProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const { ref: wrapperRef, showDropdown: isExpanded, setShowDropdown: setIsExpanded } = useShowDropdown()
  const [inputValue, setInputValue] = useState<string | number>('')

  const [filteredOptions, setFilteredOptions] = useState<SelectableOption[]>(options)

  useEffect(() => {
    // if we creating options dynamically we need to set new options when they come
    setFilteredOptions(options)
  }, [options])

  useEffect(() => {
    //sets placeholder if no option chosen
    if (value.label === '') {
      onChange({ value: '', label: placeholder || '' })
    }
  }, [value])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    if (loading === undefined) {
      if (e.target.value.toString()) {
        setFilteredOptions(() =>
          options.filter((opt) => opt.label.toLowerCase().includes(e.target.value.toString().toLowerCase()))
        )
      } else setFilteredOptions(options)
    }

    if (customInputChange) customInputChange(e)
  }

  const handleOptionChoose = (option: Option) => {
    if (!option.disabled) {
      setInputValue('') // resets input value to deactivate option filtering
      if (onChange) onChange(option) // if user provides handler
    }
  }

  return (
    <div
      ref={wrapperRef}
      className={`${s.outerWrapper} ${className}`}
      onClick={(e) => {
        e.stopPropagation()
        if (disabled) setIsExpanded(false)
      }}
    >
      {!!label && <ControlLabel labelContent={label} className={labelClassName} htmlFor={name} />}
      <div className={`${s.selectWrapper} ${isExpanded && s.noBottomRadius} ${disabled && s.disabled}`}>
        <div className={`${s.iconsAndInput} ${isExpanded && s.noBottomBorder}`}>
          {!!icon && (
            <div className={s.iconWrapper}>
              {optionsIcons?.[value?.value.toString()] ? optionsIcons?.[value?.value.toString()] : icon}
            </div>
          )}
          <input
            ref={inputRef}
            type="text"
            name={name}
            className={s.input}
            value={inputValue}
            disabled={disabled}
            placeholder={value.label}
            autoComplete="off"
            onChange={handleSearch}
          />
          <div className={s.expandIcon}>
            {loading ? (
              <Spinner small className={s.loadingSpinner} />
            ) : disabled ? (
              <LockIcon className={`${s.lockIcon}`} />
            ) : (
              <ArrowDownIcon className={`${s.arrowIcon}  ${isExpanded && s.arrowIconRotated}`} />
            )}
          </div>
        </div>
        <div className={`${s.selectMenu} ${isExpanded && s.menuVisible}`}>
          <div style={{minHeight: 0}}>
            {filteredOptions.map((option) => (
              <div
                className={`${s.option} ${option.disabled && s.optionDisabled}`}
                key={option.value}
                onClick={() => handleOptionChoose(option)}
              >
                {(optionsIcons as any)?.[option?.value] && (
                  <div className={`${s.optionIcon} ${option.value === value.value && s.iconSelected}`}>
                    {(optionsIcons as any)?.[option?.value]}
                  </div>
                )}
                <span className={`${s.optionLabel} ${option.value === value.value && s.optionSelected}`}>
                  {option.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
